/**
 * src/api/wordpressChannel.js
 */
import { getAuthHeaders } from './auth';
import { getBaseUrl as BASE } from './utils';
// FastAPI reports HTTPException as {detail: "..."} and validation errors as
// {detail: [{msg, loc}, ...]} -- neither is `message` or `error`, so every
// backend rejection used to surface as a bare "Request failed". That hid real
// causes like "already connected to another organization" behind a shrug.
//
// Only 4xx detail is shown. Those strings are copy a developer wrote FOR the
// merchant ("This Facebook page is already connected to another
// organization"). A 5xx detail is either FastAPI's own default or an
// interpolated exception carrying site URLs, DNS/TLS internals or driver
// output -- never something to put in front of a user. `loc` is dropped from
// validation errors for the same reason: it names internal request fields.
const GENERIC_ERROR = 'Something went wrong on our side. Please try again.';
const MAX_ERROR_LEN = 300;

function _errText(d, status) {
  if (status >= 500) return GENERIC_ERROR;
  const det = d?.detail;
  let msg = '';
  if (typeof det === 'string') msg = det;
  else if (Array.isArray(det)) msg = det.map(e => e?.msg || '').filter(Boolean).join('; ');
  else msg = d?.message || d?.error || '';
  msg = String(msg).trim();
  if (!msg) return GENERIC_ERROR;
  return msg.length > MAX_ERROR_LEN ? msg.slice(0, MAX_ERROR_LEN) + '…' : msg;
}

async function _parse(r) {
  const d = await r.json().catch(() => null);
  if (!r.ok) {
    // Keep the real payload reachable in devtools without showing the user.
    if (r.status >= 500) console.error('[api]', r.status, d);
    throw new Error(_errText(d, r.status));
  }
  if (d === null) throw new Error(GENERIC_ERROR);  // 2xx with a non-JSON body
  return d.status === 'success' && d.data ? d.data : (d.data ?? d);
}
function _get(p) { return fetch(`${BASE()}${p}`, { headers: { Accept: 'application/json', ...getAuthHeaders() } }).then(_parse); }
function _post(p, b) { return fetch(`${BASE()}${p}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() }, body: JSON.stringify(b) }).then(_parse); }
function _del(p) { return fetch(`${BASE()}${p}`, { method: 'DELETE', headers: { Accept: 'application/json', ...getAuthHeaders() } }).then(_parse); }

export const WORDPRESS_API_URL = `${BASE()}/bot/wordpress/connect`;
export const WORDPRESS_CONNECT_FALLBACK = WORDPRESS_API_URL;
export const WORDPRESS_DISCONNECT_URL = `${BASE()}/bot/wordpress/disconnect`;

export function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

export async function fetchWordpressFromDb() {
  try {
    const data = await _get('/bot/wordpress/account');
    if (data?.site_url) return { exists: true, wordpress_url: data.site_url, accountData: data };
    // Row may exist with only an API key (user generated a key but plugin
    // hasn't pinged back yet). Surface accountData so callers can tell the
    // difference between "no row" and "key generated, waiting for plugin".
    if (data) return { exists: false, wordpress_url: '', accountData: data };
    return { exists: false, wordpress_url: '', accountData: null };
  } catch { return { exists: false, wordpress_url: '', accountData: null }; }
}

export async function insertWordpressRow(siteUrl) {
  return _post('/bot/wordpress/connect', { site_url: siteUrl });
}

export async function deleteWordpressRow() {
  return _del('/bot/wordpress/disconnect');
}

export async function generateApiKey() {
  return _post('/bot/wordpress/generate-api-key', {});
}

export async function syncWordpress(tab) {
  return _post('/bot/wordpress/sync', { tab });
}

export async function syncAllWordpress() {
  return _post('/bot/wordpress/sync-all', {});
}

export async function savePullCredentials(wp_username, wp_app_password) {
  return fetch(`${BASE()}/bot/wordpress/pull-credentials`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ wp_username, wp_app_password }),
  }).then(_parse);
}
